import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';
import { useConfirm } from '../context/ConfirmContext.tsx';
import { scheduleAPI, profileAPI } from '../services/api.ts';
import Feed from '../components/Feed.tsx';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// HR mode: remove legacy site-manager widgets (pending signatures, maps, customer links)

// Minimal schedule type for calendar preview (HR)
interface ScheduleItem {
  id: string;
  date: string;
  time: string;
  location: string;
}

interface CheckInData {
  scheduleId: string;
  image: File | null;
  latitude: number | null;
  longitude: number | null;
}

// API parameter interface based on the server models
interface ScheduleFilterParams {
  PageNumber: number;
  PageSize: number;
  StartDate?: string;
  EndDate?: string;
  WorkFlowStatusId?: number;
}

interface SummaryData {
  monthTotal: number;
  monthCompleted: number;
  todayTotal: number;
  todayCompleted: number;
  checkInDays: number;
  checkInJobs: number;
  pendingSign: number;
}


// Mock data for demonstration - Will be replaced with API data
const mockUpcomingSchedules: ScheduleItem[] = [

];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const [upcomingSchedules, setUpcomingSchedules] = useState<ScheduleItem[]>([]);
  const [completedSchedules, setCompletedSchedules] = useState<ScheduleItem[]>([]);
  const [dashboardStats, setDashboardStats] = useState({
    checkIn: 0,
    completed: 0,
    monthCount: 0,
    loading: true,
    error: ''
  });
  const [loading, setLoading] = useState({
    upcomingSchedules: true,
    workingSchedules: true,
    completedSchedules: true
  });
  const [error, setError] = useState({
    upcomingSchedules: '',
    workingSchedules: '',
    completedSchedules: ''
  });
  const [totalUpcoming, setTotalUpcoming] = useState(0);
  const [totalCompleted, setTotalCompleted] = useState(0);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInData, setCheckInData] = useState<CheckInData>({
    scheduleId: '',
    image: null,
    latitude: null,
    longitude: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClockingOut, setIsClockingOut] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // HR: remove pending signatures/maps
  const [now, setNow] = useState<Date>(new Date());
  const [isQuickCheckInOpen, setIsQuickCheckInOpen] = useState(false);
  const [todayLog, setTodayLog] = useState<{ CreatedDateTime?: string; CheckOutDateTime?: string; Address?: string } | null>(null);
  const [stdTime, setStdTime] = useState<{ workingMorningStartTime?: string; workingMorningEndTime?: string; workingAfternoonStartTime?: string; workingAfternoonEndTime?: string; breakTime?: number } | null>(null);

  // Summary state
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Tasks state (for pending count badge)
  const [pendingTasksCount, setPendingTasksCount] = useState<number | null>(null);
  const hasInitialTaskSummaryRef = useRef(false); // Prevent double fetch in StrictMode
  const taskSummaryErrorRef = useRef(false); // Track if task summary failed to prevent retries

  // HR: no external map links on dashboard

  // HR: no WhatsApp links on dashboard

  // HR: removed map popup logic

  const getCurrentLocation = useCallback(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCheckInData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
    }
  }, []);

  const initiateCheckIn = useCallback((scheduleId: string) => {
    setCheckInData({
      scheduleId,
      image: null,
      latitude: null,
      longitude: null,
    });
    setShowCheckInModal(true);
    getCurrentLocation();
  }, [getCurrentLocation]);

  // Live clock for the attendance widget
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch today's check-in log; if user has not checked in today, open the same Check In modal as manual flow
  useEffect(() => {
    let cancelled = false;
    const d = new Date();
    const dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    scheduleAPI.getTodayCheckInLog(dateOnly)
      .then((log) => {
        if (cancelled) return;
        setTodayLog(log);
        const created =
          log &&
          ((log as { CreatedDateTime?: string; createdDateTime?: string }).CreatedDateTime ??
            (log as { createdDateTime?: string }).createdDateTime);
        if (!created) {
          initiateCheckIn('0');
        }
      })
      .catch(() => {
        if (!cancelled) setTodayLog(null);
      });
    scheduleAPI.getStandardWorkingTime()
      .then((st) => {
        if (!cancelled) setStdTime(st);
      })
      .catch(() => {
        if (!cancelled) setStdTime(null);
      });
    return () => {
      cancelled = true;
    };
  }, [initiateCheckIn]);

  // Flexible function to fetch schedules with different parameters
  // HR: Remove API calls to /schedule/GetUpcomingServices. Keep UI responsive without fetching.
  const fetchSchedules = async (_params: ScheduleFilterParams, section: 'nextVisit' | 'upcomingSchedules' | 'completedSchedules') => {
    setLoading(prev => ({ ...prev, [section]: true }));
    setError(prev => ({ ...prev, [section]: '' }));
    try {
      if (section === 'completedSchedules') {
        setCompletedSchedules([]);
        setTotalCompleted(0);
      } else if (section === 'upcomingSchedules') {
        setUpcomingSchedules([]);
        setTotalUpcoming(0);
      }
      // no API call; simply resolve
      return true;
    } finally {
      setLoading(prev => ({ ...prev, [section]: false }));
    }
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      const data = await scheduleAPI.getDashboardStats();
      setDashboardStats(prev => ({
        ...prev,
        checkIn: data.checkIn,
        completed: data.completed,
        monthCount: data.monthCount,
        loading: false,
        error: ''
      }));
    } catch (err) {
      console.error('Error fetching dashboard statistics:', err);
      setDashboardStats(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load statistics'
      }));
    }
  };


  // HR: summary box not used

  // Get current location on component mount and fetch schedules
  useEffect(() => {
    fetchDashboardStats();
    fetchSchedules({
      PageNumber: 1,
      PageSize: 5,
      StartDate: new Date().toISOString().split('T')[0],
      EndDate: new Date().toISOString().split('T')[0],
      WorkFlowStatusId: 0
    }, 'nextVisit');
    fetchSchedules({
      PageNumber: 1,
      PageSize: 5,
      StartDate: new Date().toISOString().split('T')[0],
      EndDate:new Date().toISOString().split('T')[0],
      WorkFlowStatusId: 0
    }, 'upcomingSchedules');
    
    // Fetch completed schedules for the current day
    fetchSchedules({
      PageNumber: 1,
      PageSize: 5,
      StartDate: new Date().toISOString().split('T')[0],
      EndDate: new Date().toISOString().split('T')[0],
      WorkFlowStatusId: 2
    }, 'completedSchedules');

    // Fetch pending tasks count for badge
    if (!hasInitialTaskSummaryRef.current && !taskSummaryErrorRef.current) {
      hasInitialTaskSummaryRef.current = true;
      profileAPI.getTaskSummary()
        .then((summary) => {
          setPendingTasksCount(summary.pendingCount || 0);
          taskSummaryErrorRef.current = false; // Reset error flag on success
        })
        .catch((err: any) => {
          // Check if it's a server error (404, 500, etc.)
          const status = err.response?.status || err.status;
          if (status === 404 || status === 500 || status >= 400) {
            taskSummaryErrorRef.current = true; // Mark as error to prevent retries
          }
          // Silently fail - tasks feature might not be available
          setPendingTasksCount(null);
        });
    }
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // Simply store the raw File object
      const file = e.target.files[0];
      console.log('Selected file:', file.name, file.type, `${Math.round(file.size / 1024)} KB`);
      setCheckInData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleCheckIn = async (e: React.MouseEvent, scheduleId: string) => {
    e.preventDefault();
    initiateCheckIn(scheduleId);
  };

  const handleSubmitCheckIn = async () => {
    if (!checkInData.image) {
      toast.error('Please upload an image');
      return;
    }

    if (!checkInData.latitude || !checkInData.longitude) {
      toast.error('Location data is required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Starting check-in for schedule:', checkInData.scheduleId);
      
      // Call the API with the file as binary data through fetch
      await scheduleAPI.checkIn(
        Number(checkInData.scheduleId), 
        checkInData.image,  // Pass the raw File object
        checkInData.latitude.toString(),
        checkInData.longitude.toString()
      );
      
      // Show success toast, close modal, and refresh today's log on the same screen
      toast.success('Successfully checked in!');
      setShowCheckInModal(false);

      // Optimistically update upcoming list
      setUpcomingSchedules(prev =>
        prev.map(schedule =>
          schedule.id === checkInData.scheduleId
            ? {
                ...schedule,
                startTime: new Date().toISOString(),
                workFlowStatusId: 1,
              }
            : schedule,
        ),
      );

      // Re-fetch today check-in log to update header (time, address, id)
      try {
        const d = new Date();
        const dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const latest = await scheduleAPI.getTodayCheckInLog(dateOnly);
        setTodayLog(latest);
      } catch (err) {
        // If refresh fails, at least set CreatedDateTime locally
        setTodayLog({ ...(todayLog || {}), CreatedDateTime: new Date().toISOString() } as any);
      }
      
    } catch (error: any) {
      console.error('Error checking in:', error);
      
      let errorMessage = error.message || 'Failed to check in. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // HR: removed site-manager specific helpers and actions

  // Normalize possible casing differences from API (createdDateTime vs CreatedDateTime)
  const createdAt = (todayLog as any)?.CreatedDateTime || (todayLog as any)?.createdDateTime || null;
  const checkedOutAt = (todayLog as any)?.CheckOutDateTime || (todayLog as any)?.checkOutDateTime || null;
  const checkInAddress = (todayLog as any)?.Address || (todayLog as any)?.address || '';
  const hasActiveCheckIn = !!(createdAt && !checkedOutAt);
  const alreadyClockedToday = !!(createdAt && checkedOutAt);
  const checkInDateObj = createdAt ? new Date(createdAt) : null;
  const checkOutDateObj = checkedOutAt ? new Date(checkedOutAt) : null;
  const checkInTimeStr = checkInDateObj ? checkInDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const checkInDateStr = checkInDateObj ? checkInDateObj.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const checkOutTimeStr = checkOutDateObj ? checkOutDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const checkOutDateStr = checkOutDateObj ? checkOutDateObj.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="container-app py-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* moved attendance widget to below quick actions */}

      {/* Attendance Modal */}
      {showCheckInModal && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 transition-all duration-300 animate-fadeIn native-sheet-backdrop">
          <div className="rounded-t-2xl shadow-xl overflow-hidden transform transition-transform duration-300 animate-slideUp native-bottom-sheet md:max-w-lg md:mb-4 md:rounded-2xl">
            {/* Header with close button */}
            <div className="relative bg-primary py-4 px-4">
              <h2 className="text-xl font-bold text-white text-center">Check In</h2>
              <button 
                onClick={() => setShowCheckInModal(false)}
                className="absolute right-4 top-4 text-white"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Body content */}
            <div className="p-4 native-bottom-sheet-body">
              {/* Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/heic"
                  onChange={handleImageChange}
                  ref={fileInputRef}
                  className="hidden"
                />
                
                <div 
                  className={`relative border-2 ${checkInData.image ? 'border-primary' : 'border-dashed border-gray-300'} rounded-lg overflow-hidden`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {checkInData.image ? (
                    <div className="relative">
                      <img
                        src={URL.createObjectURL(checkInData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white px-2 py-1 text-xs">
                        {checkInData.image.name} ({Math.round(checkInData.image.size / 1024)} KB)
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCheckInData(prev => ({ ...prev, image: null }));
                        }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md text-red-500"
                        aria-label="Remove image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                      <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 font-medium">Tap to take a photo</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, or HEIC</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Status */}
              <div className="flex items-center mb-5">
                <div className="mr-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-700">Location Status</div>
                  {checkInData.latitude && checkInData.longitude ? (
                    <div className="text-sm text-green-600">
                      ✓ Location captured
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-600">
                      Capturing location...
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className="border-t border-gray-200 p-4 native-bottom-sheet-footer">
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handleSubmitCheckIn}
                  disabled={isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude}
                  className={`w-full py-3 rounded-md text-white font-medium text-center ${
                    isSubmitting || !checkInData.image || !checkInData.latitude || !checkInData.longitude
                      ? 'bg-gray-400'
                      : 'bg-primary'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Check-in'}
                </button>
                
                <button
                  onClick={() => setShowCheckInModal(false)}
                  className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

     
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
      </div>

      <h2 className="text-xl font-semibold mb-4">Welcome, {user?.name}!</h2>

      {/* HR Quick Actions - card grid with icons */}
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3">
        <Link to="/tasks" className="bg-white rounded-xl shadow p-3 flex flex-col items-start relative">
            <div className="w-9 h-9 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
            </div>
            <div className="font-medium">Tasks</div>
            <div className="text-xs text-gray-500 mt-1">View & complete tasks</div>
            {pendingTasksCount !== null && pendingTasksCount > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {pendingTasksCount > 9 ? '9+' : pendingTasksCount}
              </span>
            )}
          </Link>
          <Link to="/leave" className="bg-white rounded-xl shadow p-3 flex flex-col items-start">
            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            </div>
            <div className="font-medium">Leave</div>
            <div className="text-xs text-gray-500 mt-1">Apply & view leaves</div>
          </Link>

          <Link to="/payslips" className="bg-white rounded-xl shadow p-3 flex flex-col items-start">
            <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.343-3 3h6a3 3 0 00-3-3z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14v6a2 2 0 01-2 2H7a2 2 0 01-2-2v-6z"/></svg>
            </div>
            <div className="font-medium">Documents</div>
            <div className="text-xs text-gray-500 mt-1">Download statements</div>
          </Link>

          <Link to="/transaction-request" className="bg-white rounded-xl shadow p-3 flex flex-col items-start">
            <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <div className="font-medium">Transaction</div>
            <div className="text-xs text-gray-500 mt-1">Request transactions</div>
          </Link>

          <Link to="/claims" className="bg-white rounded-xl shadow p-3 flex flex-col items-start">
            <div className="w-9 h-9 rounded-full bg-green-50 text-green-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            </div>
            <div className="font-medium">Claims</div>
            <div className="text-xs text-gray-500 mt-1">Submit expense claims</div>
          </Link>

          <Link to="/payment-verification" className="bg-white rounded-xl shadow p-3 flex flex-col items-start">
            <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <div className="font-medium">Payment Verification</div>
            <div className="text-xs text-gray-500 mt-1">Submit payment proof</div>
          </Link>

          <button onClick={() => { setShowCheckInModal(true); getCurrentLocation(); }} className="bg-white rounded-xl shadow p-3 flex flex-col items-start">
            <div className="w-9 h-9 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <div className="font-medium">Time Attendance</div>
            <div className="text-xs text-gray-500 mt-1">Clock in/out</div>
          </button>
        </div>
      </div>
      
      {/* Time Attendance - placed after quick actions */}
      <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
        <div className="px-4 py-3 bg-slate-100 flex items-center justify-between">
          <div className="text-xs text-gray-700">
            <div className="text-emerald-700 font-medium">
              {checkedOutAt
                ? `Last out ${checkOutTimeStr}, ${checkOutDateStr}`
                : (createdAt ? `Checked-in ${checkInTimeStr}, ${checkInDateStr}` : 'Not checked-in today')}
            </div>
            <div className="text-gray-600">Today’s shift</div>
          </div>
          <button
            onClick={async () => {
              const hasActive = !!(todayLog && !checkedOutAt);
              const hasAnyToday = !!todayLog; // one check-in per day
              if (hasActive) {
                const ok = await confirm('Clock out now?');
                if (!ok) return;
                try {
                  setIsClockingOut(true);
                  // Prefer checkout by id if available in todayLog
                  const idMatch = (todayLog as any)?.Id
                    || (todayLog as any)?.id
                    || (todayLog as any)?.CheckInId
                    || (todayLog as any)?.checkInId
                    || (todayLog as any)?.CheckInLogId
                    || (todayLog as any)?.checkInLogId;
                  if (idMatch) {
                    await scheduleAPI.clockOutById(Number(idMatch));
                  } else {
                    await scheduleAPI.clockOut();
                  }
                  toast.success('Clocked out successfully');
                  // Refresh today's log from server to update status/time
                  try {
                    const d = new Date();
                    const dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    const latest = await scheduleAPI.getTodayCheckInLog(dateOnly);
                    setTodayLog(latest);
                  } catch {
                    // Fallback: set checkout time locally
                    setTodayLog(prev => prev ? { ...prev, CheckOutDateTime: new Date().toISOString() } : prev);
                  }
                } catch {
                  toast.error('Failed to clock out');
                } finally {
                  setIsClockingOut(false);
                }
                return;
              }
              if (hasAnyToday) {
                toast.info('You have already clocked today. Only one check-in per day is allowed.');
                return;
              }
              setShowCheckInModal(true); 
              getCurrentLocation();
            }}
            className={`px-4 py-2 rounded-md font-medium ${
              hasActiveCheckIn
                ? (isClockingOut
                    ? 'bg-white text-primary border border-primary'
                    : 'bg-primary text-white')
                : (alreadyClockedToday ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-primary text-white')
            }`}
            disabled={alreadyClockedToday || (hasActiveCheckIn && isClockingOut)}
          >
            {hasActiveCheckIn ? (
              isClockingOut ? (
                <span className="inline-flex items-center">
                  <span className="inline-block animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mr-2"></span>
                  Clocking out…
                </span>
              ) : (
                'Clock-Out'
              )
            ) : (
              alreadyClockedToday ? 'Clocked Today' : 'Clock-In'
            )}
          </button>
        </div>
        <div className="px-4 py-3">
          <div className="flex items-start justify-between">
            <div className="bg-gray-100 rounded px-2 py-1 text-sm">Recommended</div>
            <div className="text-sm text-gray-800 text-right">
              {stdTime ? (
                <>
                  <div>
                    <span className="font-medium">{stdTime.workingMorningStartTime}</span> - {stdTime.workingMorningEndTime}
                  </div>
                  <div>
                    <span className="font-medium">{stdTime.workingAfternoonStartTime}</span> - {stdTime.workingAfternoonEndTime}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Break hour: <span className="font-medium">{stdTime.breakTime}h</span></div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="inline-block animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
            </div>
          </div>
          {/* Show today's check-in details when present */}
          {createdAt && (
            <div className="mt-3 text-sm text-gray-700">
              <div>
                Checked-in at <span className="font-medium">{checkInTimeStr}</span>
                <span className="ml-1 text-xs text-gray-500">{checkInDateStr}</span>
              </div>
              {checkInAddress ? (
                <div className="text-xs text-gray-600 truncate">{checkInAddress}</div>
              ) : null}
            </div>
          )}
          <div className="text-xs text-gray-600 mt-2">Date: {now.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</div>
        </div>
      </div>

    
      {/* Feed */}
      <Feed />
      
      {/* HR: removed pending signatures section */}

      {/* HR: removed map picker modal */}
    </div>
  );
};

export default Dashboard; 
